import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaPaperPlane, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { API_BASE } from '../config';
import './ContactPage.css';

const setMeta = (seo) => {
  if (!seo) return;
  if (seo.metaTitle) document.title = seo.metaTitle;
  const setOrCreate = (attr, attrVal, content) => {
    if (!content) return;
    let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
    if (!el) { el = document.createElement('meta'); el.setAttribute(attr, attrVal); document.head.appendChild(el); }
    el.setAttribute('content', content);
  };
  setOrCreate('name', 'description', seo.metaDescription);
  setOrCreate('name', 'keywords', seo.metaKeywords);
  setOrCreate('property', 'og:title', seo.metaTitle);
  setOrCreate('property', 'og:description', seo.metaDescription);
  if (seo.ogImage) setOrCreate('property', 'og:image', seo.ogImage);
};

const defaults = {
  hero: { badge: 'Bize Ulaşın', title: 'İletişim', description: 'Soru, öneri ve talepleriniz için bizimle iletişime geçebilirsiniz' },
  info: { address: 'Cumhuriyet Mah. 19 Mayıs Cad. No:36/1 İtfaiye Binası, Didim/Aydın', phone: '0546 664 16 02', email: 'info@dihiz.com', workingHours: 'Pazartesi - Cumartesi: 09:00 - 18:00' },
  map: { embedUrl: '' },
  form: { title: 'Mesaj Gönderin', description: 'Formu doldurarak bize mesaj gönderebilirsiniz.', successMessage: 'Mesajınız başarıyla gönderildi.' },
};

const ContactPage = () => {
  const [data, setData] = useState(defaults);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [modal, setModal] = useState({ open: false, type: '', message: '' });

  useEffect(() => {
    fetch(`${API_BASE}/contact`)
      .then(r => r.json())
      .then(api => {
        if (api && api._id) {
          setData(prev => ({
            hero: { ...prev.hero, ...api.hero },
            info: { ...prev.info, ...api.info },
            map: { ...prev.map, ...api.map },
            form: { ...prev.form, ...api.form },
          }));
          setMeta(api.seo);
        }
      })
      .catch(() => {});
    return () => { document.title = 'Dihiz'; };
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setModal({ open: true, type: 'error', message: 'Lütfen ad, e-posta ve mesaj alanlarını doldurun.' });
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/contact-messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error();
      setModal({ open: true, type: 'success', message: data.form.successMessage });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      setModal({ open: true, type: 'error', message: 'Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin.' });
    }
    setSending(false);
  };

  const { hero, info, map, form } = data;

  return (
    <div className="contact-page">
      {/* Modal */}
      {modal.open && (
        <div className="contact-modal-overlay" onClick={() => setModal({ ...modal, open: false })}>
          <div className="contact-modal" onClick={e => e.stopPropagation()}>
            <div className={`contact-modal-icon ${modal.type}`}>
              {modal.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
            </div>
            <h3 className="contact-modal-title">
              {modal.type === 'success' ? 'Başarılı!' : 'Hata'}
            </h3>
            <p className="contact-modal-message">{modal.message}</p>
            <button className="contact-modal-btn" onClick={() => setModal({ ...modal, open: false })}>
              Tamam
            </button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="contact-hero">
        <div className="contact-hero-overlay"></div>
        <div className="contact-hero-content">
          <span className="contact-hero-badge">{hero.badge}</span>
          <h1 className="contact-hero-title">{hero.title}</h1>
          <p className="contact-hero-desc">{hero.description}</p>
        </div>
      </section>

      {/* Info Cards */}
      <section className="contact-info-section">
        <div className="contact-info-container">
          <div className="contact-info-grid">
            <div className="contact-info-card">
              <div className="contact-info-icon"><FaMapMarkerAlt /></div>
              <h4>Adres</h4>
              <p>{info.address}</p>
            </div>
            <div className="contact-info-card">
              <div className="contact-info-icon"><FaPhone /></div>
              <h4>Telefon</h4>
              <p><a href={`tel:${info.phone?.replace(/\s/g, '')}`}>{info.phone}</a></p>
            </div>
            <div className="contact-info-card">
              <div className="contact-info-icon"><FaEnvelope /></div>
              <h4>E-posta</h4>
              <p><a href={`mailto:${info.email}`}>{info.email}</a></p>
            </div>
            <div className="contact-info-card">
              <div className="contact-info-icon"><FaClock /></div>
              <h4>Çalışma Saatleri</h4>
              <p>{info.workingHours}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="contact-main">
        <div className="contact-form-container">
          <div className="contact-form-wrapper">
            <h2 className="contact-form-title">{form.title}</h2>
            <p className="contact-form-desc">{form.description}</p>
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="contact-form-row">
                <div className="contact-field">
                  <label>Ad Soyad *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Adınız Soyadınız" />
                </div>
                <div className="contact-field">
                  <label>E-posta *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="ornek@email.com" />
                </div>
              </div>
              <div className="contact-form-row">
                <div className="contact-field">
                  <label>Telefon</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="05XX XXX XX XX" />
                </div>
                <div className="contact-field">
                  <label>Konu</label>
                  <input type="text" name="subject" value={formData.subject} onChange={handleChange} placeholder="Mesaj konusu" />
                </div>
              </div>
              <div className="contact-field">
                <label>Mesajınız *</label>
                <textarea name="message" value={formData.message} onChange={handleChange} rows={5} placeholder="Mesajınızı buraya yazın..." />
              </div>
              <button type="submit" className="contact-submit-btn" disabled={sending}>
                {sending ? 'Gönderiliyor...' : <><FaPaperPlane /> Mesaj Gönder</>}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Map - Full Width */}
      {map.embedUrl && (
        <section className="contact-map-section">
          <iframe
            src={map.embedUrl}
            title="Konum"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </section>
      )}
    </div>
  );
};

export default ContactPage;
